import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { userErrorMessage } from "@/lib/user-error";
import { Link, useParams } from "react-router";
import { useQuery } from "@apollo/client/react";
import { BackButton } from "@/components/common/BackButton";
import { FilterChip } from "@/components/design/FilterChip";
import { LoadMore } from "@/components/common/LoadMore";
import { NoBackend } from "@/components/common/NoBackend";
import { ShareItemCardFeed } from "@/components/share/ShareItemCard";
import { ShareSortSelect } from "@/components/share/ShareSortSelect";
import { personalItemsOfWhere } from "@/lib/search-filters";
import { sortOrderFor, type SortKey } from "@/lib/share-sort";
import { usePagedQuery } from "@/lib/use-paged-query";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { useAssistantTour } from "@/lib/use-assistant-tour";
import {
  PersonsByIdsDocument,
  SharedItemsDocument,
  type SharedItemsQuery,
} from "@/graphql/generated/operations";
import type { SharedItemType } from "@/graphql/generated/operations";

type SharedItemNode = SharedItemsQuery["sharedItems"]["nodes"][number];

// SHARE · Meine Beiträge - same ShareItemCardFeed template as the Share feed
// but scoped to the user's own items (owner filter). FilterChips at the top
// narrow the visible set by type (all / Projekte / Posts). Single-select with
// "All" as the no-filter default per the ambient-filter convention. Filtering
// is applied client-side so switching is instant and refetch-free.
//
// TWO MODES, ONE PAGE (spark#276). With a `:personId` in the route this is somebody else's
// share page instead: same grid, same chips, same sorting, same paging, only the author and
// the wording differ. It is deliberately not a second component. The two modes ask the same
// question about a different author, and a copy drifts from its original within a release,
// which is how the same list ends up sorting differently in two places.
//
// The two modes reach their rows through DIFFERENT filters, and that is not an inconsistency:
// `owner` is genesis's own "mine" predicate for the caller, `createdBy` is the general one.
// See personalItemsOfWhere in search-filters.ts for why the foreign mode also has to exclude
// what the person published in a company's name.

const FILTERS = [
  { id: "all" as const, labelId: "share.mine.filterAll", labelDefault: "All" },
  { id: "PROJECT" as const, labelId: "share.mine.filterProjects", labelDefault: "Projects" },
  { id: "POST" as const, labelId: "share.mine.filterPosts", labelDefault: "Posts" },
];
type Filter = (typeof FILTERS)[number]["id"];

export function ShareMinePage() {
  const intl = useIntl();
  const { personId } = useParams();
  const viewingOther = Boolean(personId);
  const [filter, setFilter] = useState<Filter>("all");
  const [sort, setSort] = useState<SortKey>("new");

  // Only the byline is needed, so the page reuses the slim persons-by-id query rather than the
  // full profile one: somebody arriving from a link should not pay for a whole profile to read
  // a heading. Coming from /p/:id the Person is already in the Apollo cache either way.
  const personQuery = useQuery(PersonsByIdsDocument, {
    variables: { input: { first: 1, where: { id: { eq: personId ?? "" } } } },
    skip: !viewingOther,
  });
  const person = personQuery.data?.persons.nodes[0] ?? null;
  const personName = person?.formattedName ?? "";
  // TWO WAYS TO HAVE NO PERSON, AND THEY MUST NOT SHARE A MESSAGE. genesis answers "unknown" and
  // "not shared with you" identically, so one message covers those two. A failed request is a third
  // thing: saying "profile not found" then makes a claim about the person out of what is a claim
  // about the connection. ProfilePage draws the same line for the same reason.
  const personFailed = viewingOther && Boolean(personQuery.error);
  const personMissing = viewingOther && !personQuery.loading && !person && !personQuery.error;

  const input = useMemo(
    () => ({
      order: sortOrderFor(sort),
      where: personId ? personalItemsOfWhere(personId) : { owner: { eq: true } },
    }),
    [sort, personId],
  );
  const {
    nodes,
    loading,
    error,
    hasMore,
    loadingMore,
    loadMoreFailed,
    loadMore,
  } = usePagedQuery<SharedItemNode>(SharedItemsDocument, {
    connectionKey: "sharedItems",
    input,
    pageSize: 24,
  });

  const items = useMemo(() => {
    if (filter === "all") return nodes;
    return nodes.filter((i) => i.itemType === (filter as SharedItemType));
  }, [nodes, filter]);

  // The heading only becomes personal once the name is in. Rendering "Posts by " with a blank
  // where the name goes is worse than a generic heading for the same fraction of a second.
  const title = viewingOther
    ? personName
      ? intl.formatMessage(
          { id: "share.person.title", defaultMessage: "Posts by {name}" },
          { name: personName },
        )
      : intl.formatMessage({ id: "share.person.titleUnknown", defaultMessage: "Posts" })
    : intl.formatMessage({ id: "share.mine.title", defaultMessage: "My posts" });

  // Live assistant context (motive 1, @external-adr AGENT-SERVICE-0020-ag-ui-protocol-adoption):
  // the shared items on screen, said as what they are in this mode. An assistant told "the
  // user's own" while a foreign page is open answers the wrong question with total confidence.
  useAssistantContext(
    loading && !nodes.length
      ? null
      : [
          {
            description: viewingOther
              ? "another person's shared items page"
              : "the user's own shared items page",
            value:
              (viewingOther
                ? `${items.length} shared items by ${personName || "that person"} that this viewer may see`
                : `${items.length} own shared items`) +
              (filter !== "all" ? ` (filtered to ${filter})` : ""),
          },
        ],
  );

  // Guided tour (/tour): the type filter + the items grid, worded per mode.
  useAssistantTour(
    loading && !nodes.length
      ? null
      : {
          label: viewingOther
            ? intl.formatMessage({ id: "share.person.tour.label", defaultMessage: "Share Person" })
            : intl.formatMessage({ id: "share.mine.tour.label", defaultMessage: "Share Mine" }),
          summary: viewingOther
            ? intl.formatMessage(
                {
                  id: "share.person.tour.summary",
                  defaultMessage:
                    "What {name} has published on Share and you can see. There are {count} items here right now.",
                },
                { name: personName, count: items.length },
              )
            : intl.formatMessage(
                {
                  id: "share.mine.tour.summary",
                  defaultMessage:
                    "These are your own posts, everything you have published on Share. You have {count} items here right now.",
                },
                { count: items.length },
              ),
          stops: [
            {
              anchor: "share.mine.filters",
              title: intl.formatMessage({
                id: "share.mine.tour.filters.title",
                defaultMessage: "Filter & sort",
              }),
              description: intl.formatMessage({
                id: "share.mine.tour.filters.desc",
                defaultMessage:
                  "Narrow the list to Projects or Posts, and change the sort order on the right.",
              }),
            },
            {
              anchor: "share.mine.items",
              title: viewingOther
                ? intl.formatMessage({
                    id: "share.person.tour.items.title",
                    defaultMessage: "Their items",
                  })
                : intl.formatMessage({
                    id: "share.mine.tour.items.title",
                    defaultMessage: "Your items",
                  }),
              description: viewingOther
                ? intl.formatMessage(
                    {
                      id: "share.person.tour.items.desc",
                      defaultMessage: "{count} items. Click one to open it.",
                    },
                    { count: items.length },
                  )
                : items.length === 0
                  ? intl.formatMessage({
                      id: "share.mine.tour.items.emptyDesc",
                      defaultMessage:
                        "Nothing here yet. Create a project or a post and it will show up in this grid.",
                    })
                  : intl.formatMessage(
                      {
                        id: "share.mine.tour.items.desc",
                        defaultMessage:
                          "{count} items. Click one to open it; as the owner you can edit or delete it from its detail page.",
                      },
                      { count: items.length },
                    ),
            },
          ],
        },
  );

  if (personMissing || personFailed) {
    const { label, hint } = personFailed
      ? {
          label: intl.formatMessage({
            id: "share.person.loadFailed",
            defaultMessage: "Profile could not be loaded",
          }),
          hint: userErrorMessage(intl, personQuery.error),
        }
      : {
          label: intl.formatMessage({
            id: "share.person.notFound",
            defaultMessage: "Profile not found",
          }),
          hint: intl.formatMessage({
            id: "share.person.notFoundHint",
            defaultMessage: "This profile does not exist or has not been shared with you.",
          }),
        };

    return (
      <div className="max-w-[1400px] px-4 pb-12 pt-2 @sm:px-6 @lg:px-10">
        <div className="mb-5">
          <BackButton />
        </div>
        <NoBackend label={label} hint={hint} />
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] px-4 pb-12 pt-2 @sm:px-6 @lg:px-10" data-graphql="SharedItems">
      <div className="mb-5">
        <BackButton />
      </div>
      <div className="mb-4 text-smycles-micro tracking-[0.25em] text-ink-muted">
        {viewingOther ? (
          <FormattedMessage id="share.person.eyebrow" defaultMessage="SHARE · Posts" />
        ) : (
          <FormattedMessage id="share.mine.eyebrow" defaultMessage="SHARE · My posts" />
        )}
      </div>
      <h1 className="smycles-page-title">{title}</h1>
      <p className="mt-4 max-w-md text-smycles-body text-ink">
        {viewingOther ? (
          personName ? (
            <FormattedMessage
              id="share.person.subtitle"
              defaultMessage="Projects and posts by {name} that you can see."
              values={{ name: personName }}
            />
          ) : null
        ) : (
          <FormattedMessage id="share.mine.subtitle" defaultMessage="All your projects and posts." />
        )}
      </p>

      <div className="mt-6 flex flex-wrap items-center gap-2" data-assistant-anchor="share.mine.filters">
        {FILTERS.map((f) => (
          <FilterChip key={f.id} active={f.id === filter} onClick={() => setFilter(f.id)}>
            {intl.formatMessage({ id: f.labelId, defaultMessage: f.labelDefault })}
          </FilterChip>
        ))}
        <div className="ml-auto">
          <ShareSortSelect value={sort} onChange={setSort} />
        </div>
      </div>

      <div className="mt-8" data-assistant-anchor="share.mine.items">
        {loading ? (
          <div className="text-smycles-label text-ink-muted">
            <FormattedMessage id="common.loading" defaultMessage="Loading…" />
          </div>
        ) : error ? (
          <div className="text-smycles-label text-ink-muted">
            <FormattedMessage
              id="share.mine.error"
              defaultMessage="Error: {message}"
              values={{ message: userErrorMessage(intl, error) }}
            />
          </div>
        ) : items.length === 0 ? (
          <NoBackend
            id="share-mine-empty"
            // NOT "{name} has not published anything yet". On a foreign page an empty grid has four
            // causes and only one of them is that sentence: the person may have published only in a
            // company's name (excluded here by design), their items may be tagged for an audience
            // this reader is not in, or the reader may not be allowed to learn who wrote what at all
            // (genesis withholds the byline from a reader who is neither owner nor internal, and its
            // author filter withholds the rows with it). A message about the person would be a
            // guess; a message about what is on screen is always true.
            label={
              viewingOther
                ? intl.formatMessage({ id: "share.person.emptyLabel", defaultMessage: "No posts to show" })
                : intl.formatMessage({ id: "share.mine.emptyLabel", defaultMessage: "No own posts yet" })
            }
            // No hint on a foreign page: the own one tells you to go and create something,
            // which is not advice a visitor can act on.
            hint={
              viewingOther
                ? undefined
                : intl.formatMessage({
                    id: "share.mine.emptyHint",
                    defaultMessage: "create a project or a post",
                  })
            }
          />
        ) : (
          <>
            <div id="share-mine-list" className="grid grid-cols-1 gap-4 @sm:grid-cols-2 @lg:grid-cols-4">
              {items.map((item, i) => (
                <Link
                  key={item.id}
                  to={`/share/discover/${item.id}`}
                  className="block transition hover:-translate-y-0.5"
                >
                  <ShareItemCardFeed item={item} index={i} />
                </Link>
              ))}
            </div>
            <LoadMore hasMore={hasMore} loading={loadingMore} paused={loadMoreFailed} onLoadMore={loadMore} />
          </>
        )}
      </div>
    </div>
  );
}
